import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { MessageCircle, Mail, Smartphone, Copy } from "lucide-react";

// Jediné místo, kde vzniká veřejný odkaz — používá ho dialog i přímé tlačítko
// WhatsApp v liště výběru. Vyhazuje chybu, hlášku řeší volající.
export async function createShareUrl(ids) {
  const r = await api.post("/photos/share", { ids, ttl_days: 7 });
  return r.data.url;
}
export const shareText = (u) => `Fotky ze stavby: ${u}`;

// 185: ⋯ › Sdílet na WhatsApp v prohlížeči fotek. Okno otevřeme hned při klepnutí
// (iPhone jinak okno po čekání na odkaz zablokuje) a adresu doplníme, až odkaz vznikne.
export async function shareToWhatsApp(ids) {
  const w = window.open("", "_blank");
  try {
    const u = await createShareUrl(ids);
    const href = `https://wa.me/?text=${encodeURIComponent(shareText(u))}`;
    if (w) w.location.href = href; else window.location.href = href;
  } catch (e) {
    if (w) w.close();
    toast.error(formatApiError(e.response?.data?.detail) || "Odkaz se nepodařilo vytvořit.");
  }
}

// Vytvoří veřejný odkaz (POST /photos/share) a nabídne rozeslání. Odkazy skládá
// frontend; příjemce nepotřebuje účet (veřejná routa /s/:token).
export function ShareSheet({ ids, onClose }) {
  const [url, setUrl] = useState(null);
  const [busy, setBusy] = useState(false);

  const make = async () => {
    setBusy(true);
    try {
      const u = await createShareUrl(ids);
      setUrl(u);
      return u;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return null; }
    finally { setBusy(false); }
  };

  const withUrl = async (fn) => { const u = url || (await make()); if (u) fn(u); };
  const text = shareText;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="share-sheet" className="max-w-[420px]">
        <DialogHeader><DialogTitle>Poslat fotky odkazem</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Odkaz platí 7 dní. Příjemce nepotřebuje účet.</p>
        <div className="grid grid-cols-2 gap-2 mt-1">
          <Button variant="outline" className="h-12 justify-start" disabled={busy} data-testid="share-whatsapp"
            onClick={() => withUrl((u) => window.open(`https://wa.me/?text=${encodeURIComponent(text(u))}`, "_blank"))}><MessageCircle size={16} className="mr-2 text-brand" /> WhatsApp</Button>
          <Button variant="outline" className="h-12 justify-start" disabled={busy} data-testid="share-sms"
            onClick={() => withUrl((u) => { window.location.href = `sms:?body=${encodeURIComponent(text(u))}`; })}><Smartphone size={16} className="mr-2 text-brand" /> SMS</Button>
          <Button variant="outline" className="h-12 justify-start" disabled={busy} data-testid="share-email"
            onClick={() => withUrl((u) => { window.location.href = `mailto:?subject=${encodeURIComponent("Fotky ze stavby")}&body=${encodeURIComponent(text(u))}`; })}><Mail size={16} className="mr-2 text-brand" /> E-mail</Button>
          <Button variant="outline" className="h-12 justify-start" disabled={busy} data-testid="share-copy"
            onClick={() => withUrl(async (u) => { try { await navigator.clipboard.writeText(u); toast.success("Odkaz zkopírován."); } catch { toast.error("Kopírování se nepovedlo."); } })}><Copy size={16} className="mr-2 text-brand" /> Kopírovat odkaz</Button>
        </div>
        {url && <div className="mt-3 text-xs break-all bg-secondary rounded-lg p-2" data-testid="share-url">{url}</div>}
      </DialogContent>
    </Dialog>
  );
}
